import { ActionTree } from 'vuex';
import { ChatState, FontSettingsType } from './types';
import { RootState } from '@/store/types';
import {
  SET_CHAT_USER,
  SET_ME_RESOLVED,
  SET_ROOM_IS_ACTIVE,
  ADD_NEW_MESSAGE,
  EDIT_MESSAGE,
  UPDATE_GUEST_IS_TYPING,
  UPDATE_USER_IS_PAUSED,
  SET_USERS,
  SET_MESSAGES,
  CHANGE_FONT_SETTING,
  SET_SETTINGS_USER,
  CHANGE_IS_PAUSED_RECOGNITION,
  CHANGE_IS_ACTIVE_RECOGNITION,
  CHANGE_IS_CAMERA_DOUBLE_WIDTH,
  CHANGE_USER_AUDIO_DEVICE_STATUS,
  CHANGE_USER_VIDEO_DEVICE_STATUS,
  SET_IS_SIGNER_GUIDE_ACCEPTED,
  SET_CONVERSATION_INFO,
  SET_RECOGNITION_MODELS,
} from './mutationTypes';
import {
  SystemMessage,
  Message,
  User,
  MessagesData,
  DeviceStatusEnum,
  RecognitionModelInfo,
} from '@/types';
import { ConversationInfo } from '@/pages/Start/types';
import { LocalStorageConsts } from '@/constants/localstorage';

const saveUser = (user: User) => {
  const userString = localStorage.getItem('user');
  if (userString) {
    const oldUser = JSON.parse(userString);
    if (oldUser.id === user.id) {
      localStorage.setItem(
        'user',
        JSON.stringify({
          ...oldUser,
          ...user,
        }),
      );
      return;
    }
  }

  localStorage.setItem('user', JSON.stringify(user));
};

const getUser = () => {
  try {
    const userString = localStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
};

const saveUserSettings = (fontSettings: FontSettingsType) => {
  localStorage.setItem('settings', JSON.stringify(fontSettings));
};

const saveIsCameraDoubleWidth = (isDouble: boolean) => {
  localStorage.setItem('IsCameraDoubleWidth', JSON.stringify(isDouble));
};

const getIsCameraDoubleWidth = () => {
  const value = localStorage.getItem('IsCameraDoubleWidth');
  if (value === 'true') {
    return true;
  } else {
    return false;
  }
};

const getUserSettings = () => {
  try {
    const settingsString = localStorage.getItem('settings');
    if (settingsString) {
      const settings = JSON.parse(settingsString);
      return settings;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
};

export const actions: ActionTree<ChatState, RootState> = {
  clearGuestUser({ commit }) {
    const userInLocalStore = getUser();
    if (!userInLocalStore.isAuthorized) {
      localStorage.removeItem('user');
      commit(SET_CHAT_USER, {
        id: '',
        firstName: '',
        lastName: '',
        userMode: '',
        paused: false,
        isAcknowledged: null,
        isAuthorized: false,
        email: '',
        audioDeviceStatus: DeviceStatusEnum.active,
        videoDeviceStatus: DeviceStatusEnum.active,
      });
    }
  },

  setConversationInfo({ commit }, info: ConversationInfo) {
    commit(SET_CONVERSATION_INFO, info);
  },

  clearConversationInfo({ commit }) {
    commit(SET_CONVERSATION_INFO, { id: '', code: '' });
  },

  loadUserFromStorage({ commit }) {
    const user = getUser();
    if (user?.id) {
      commit(SET_CHAT_USER, user);
    }
    const settings = getUserSettings();
    if (settings) {
      commit(SET_SETTINGS_USER, settings);
    }
  },

  changeIsPausedRecognizing({ commit }, value: boolean) {
    commit(CHANGE_IS_PAUSED_RECOGNITION, value);
  },

  socket_roomUsersUpdated({ commit }, users: User[]) {
    commit(SET_USERS, users);
  },

  socket_roomStatusUpdated({ commit }, active: boolean) {
    commit(SET_ROOM_IS_ACTIVE, active);
  },

  socket_systemMessage(_, msg: SystemMessage) {
    console.info(msg);
  },

  socket_newMessage({ commit }, message: Message) {
    commit(ADD_NEW_MESSAGE, message);
  },

  socket_roomInfoUpdated(
    { commit },
    data: {
      users: User[];
      messagesData: MessagesData;
      active: boolean;
      conversationInfo: ConversationInfo;
    },
  ) {
    commit(SET_ROOM_IS_ACTIVE, data.active);
    commit(SET_USERS, data.users);
    commit(SET_MESSAGES, data.messagesData);
    commit(SET_CONVERSATION_INFO, data.conversationInfo);
  },

  socket_editedMessage({ commit }, message: Message) {
    commit(EDIT_MESSAGE, message);
  },

  socket_getPreviousMessages({ commit, state }, messagesData: MessagesData) {
    const { messages, allMessagesCount } = messagesData;
    const data = { messages: messages.concat(state.messages), allMessagesCount };
    commit(SET_MESSAGES, data);
  },

  socket_userAcknowledgementChanged({ dispatch, state }, value: boolean) {
    dispatch('userUpdated', {
      ...state.me,
      isAcknowledged: value,
    });
  },

  socket_userTypingStatusChanged(
    { dispatch, state },
    payload: { userId: string; isTyping: boolean },
  ) {
    if (payload.userId === state.me?.id) {
      return;
    }
    if (payload.isTyping) {
      dispatch('changeIsTypingStatus', payload.userId);
      return;
    }

    if (!payload.isTyping) {
      dispatch('changeIsTypingStatus', '');
    }
  },

  socket_userUpdated({ commit, dispatch }, user: User) {
    dispatch('userUpdated', user);
    // Only this path counts: loadUserFromStorage fills `me` from the last session,
    // which is enough to render but not to decide anything the server owns.
    commit(SET_ME_RESOLVED, true);
  },

  changeIsTypingStatus({ commit }, idGuest: string) {
    commit(UPDATE_GUEST_IS_TYPING, idGuest);
  },

  userUpdated({ commit }, user: User) {
    commit(SET_CHAT_USER, user);
    saveUser(user);
  },

  socket_userIsPaused({ dispatch }, payload: { userId: string; paused: boolean }) {
    dispatch('changeUserIsPaused', payload);
  },

  socket_userChangedAudioDeviceStatus(
    { commit },
    payload: { userId: string; status: DeviceStatusEnum },
  ) {
    commit(CHANGE_USER_AUDIO_DEVICE_STATUS, payload);
  },

  socket_userChangedVideoDeviceStatus(
    { commit },
    payload: { userId: string; status: DeviceStatusEnum },
  ) {
    commit(CHANGE_USER_VIDEO_DEVICE_STATUS, payload);
  },

  changeUserIsPaused({ commit }, payload: { userId: string; paused: boolean }) {
    commit(UPDATE_USER_IS_PAUSED, payload);
  },

  changeFontSetting({ commit, state }, payload: { key: string; value: number }) {
    commit(CHANGE_FONT_SETTING, payload);
    saveUserSettings(state.fontSettings);
  },

  socket_recognitionLaunched({ commit }) {
    commit(CHANGE_IS_ACTIVE_RECOGNITION, { isActive: true });
  },

  socket_recognitionDisabled({ commit }, warning: string) {
    commit(CHANGE_IS_ACTIVE_RECOGNITION, { isActive: false, warning });
  },

  setIsCameraDoubleWidth({ commit }) {
    const isDouble = getIsCameraDoubleWidth();
    commit(CHANGE_IS_CAMERA_DOUBLE_WIDTH, isDouble);
  },

  changeIsCameraDoubleWidth({ commit }, isDouble: boolean) {
    commit(CHANGE_IS_CAMERA_DOUBLE_WIDTH, isDouble);
    saveIsCameraDoubleWidth(isDouble);
  },

  setIsSignerGuideAccepted({ commit }, isAccepted: boolean) {
    commit(SET_IS_SIGNER_GUIDE_ACCEPTED, isAccepted);
    localStorage.setItem(LocalStorageConsts.SIGNER_GUIDE_ACCEPTED, JSON.stringify(isAccepted));
  },

  socket_recognitionModels({ commit }, payload: { models: RecognitionModelInfo[] }) {
    commit(SET_RECOGNITION_MODELS, payload.models);
  },
};
